/**
 * ContactRequestsPanel — accepted contacts, incoming requests, and (DEV-1485)
 * the requests you have SENT that are still awaiting a reply.
 *
 * Three sections:
 *   1. My Contacts — accepted contacts with "..." overflow menu (View Profile, Chat, Remove, Block)
 *   2. Pending Requests — INCOMING requests with Accept/Reject
 *   3. Sent Requests (DEV-1485) — OUTGOING requests, awaiting the other person;
 *      Cancel only, since they are not yours to accept. Hidden when empty.
 *
 * Blocked users are NOT shown here — they're managed via Account Settings.
 *
 * Used inside MessagesPage's "Contacts" tab.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchContacts, removeContact, blockUser } from '../../api/chat';
import type { ContactResponse } from '../../api/chat';
import UserAvatar from '../UserAvatar/UserAvatar';
import styles from './Chat.module.css';

interface ContactRequestsPanelProps {
  requests: ContactResponse[];
  /** DEV-1485 — pending OUTGOING requests (I asked; awaiting their answer). */
  sentRequests?: ContactResponse[];
  currentUserGuid: string;
  onAccept: (contactId: string) => void;
  onReject: (contactId: string) => void;
  onStartChat: (userGuid: string) => void;
}

export default function ContactRequestsPanel({
  requests,
  sentRequests = [],
  currentUserGuid,
  onAccept,
  onReject,
  onStartChat,
}: ContactRequestsPanelProps) {
  const { t } = useAppTranslation('chat');
  const navigate = useNavigate();
  const [acceptedContacts, setAcceptedContacts] = useState<ContactResponse[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'remove' | 'block'; contactId: string; userGuid: string; name: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenuId]);

  // Load accepted contacts
  useEffect(() => {
    setLoadingContacts(true);
    fetchContacts()
      .then((contacts) => {
        setAcceptedContacts(contacts);
        setLoadingContacts(false);
      })
      .catch(() => {
        setLoadingContacts(false);
      });
  }, [requests.length]); // Refresh when pending requests change (after accept)

  /** Get the "other" user's guid from a contact record. */
  const getOtherUserGuid = (c: ContactResponse): string =>
    c.requesterGuid.toUpperCase() === currentUserGuid.toUpperCase()
      ? c.recipientGuid
      : c.requesterGuid;

  const handleRemoveContact = async (contactId: string) => {
    try {
      await removeContact(contactId);
      setAcceptedContacts((prev) => prev.filter((c) => c.id !== contactId));
      setConfirmAction(null);
    } catch (err) {
      console.error('[ContactRequestsPanel] Failed to remove contact:', err);
    }
  };

  const handleBlockUser = async (userGuid: string) => {
    try {
      await blockUser(userGuid);
      setAcceptedContacts((prev) => prev.filter((c) => getOtherUserGuid(c) !== userGuid));
      setConfirmAction(null);
    } catch (err) {
      console.error('[ContactRequestsPanel] Failed to block user:', err);
    }
  };

  /* ── DEV-1486 — one list at a time, searchable ──────────────────
     The old layout stacked all three lists, each with `flex: 1`, so every
     section took a third of the panel height REGARDLESS of content: "Pending 0"
     burned ~400px on an empty state while Sent Requests sat below the fold. At
     200 contacts it degrades further — the actionable requests end up buried
     under an endless directory.

     The three lists have different jobs, so they get one surface each:
       Contacts — a DIRECTORY. You arrive knowing who you want → search first.
       Pending  — an INBOX. The only actionable one → its count must nag from
                  the segment, visible without scrolling anywhere.
       Sent     — a STATUS CHECK. Glanced at, occasionally cancelled. */
  const [view, setView] = useState<'contacts' | 'pending' | 'sent'>('contacts');
  const [query, setQuery] = useState('');

  const matches = useCallback((c: ContactResponse) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${c.displayName || ''} ${c.screenName || ''}`.toLowerCase().includes(q);
  }, [query]);

  const filteredAccepted = acceptedContacts.filter(matches);
  const filteredPending = requests.filter(matches);
  const filteredSent = sentRequests.filter(matches);

  /* Pending is the only list that can demand action, so it opens itself when
     something is waiting and the user hasn't chosen a view yet. One-shot: after
     it fires once, the user's own segment choice is never overridden. */
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (requests.length > 0) {
      autoOpenedRef.current = true;
      setView('pending');
    }
  }, [requests.length]);

  const segments: Array<{ key: 'contacts' | 'pending' | 'sent'; label: string; count: number }> = [
    { key: 'contacts', label: t('contacts.accepted'), count: acceptedContacts.length },
    { key: 'pending',  label: t('contacts.pending'),  count: requests.length },
    { key: 'sent',     label: t('contacts.sent'),     count: sentRequests.length },
  ];

  const menuItemStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8,
    width: '100%', padding: '9px 14px',
    background: 'none', border: 'none', textAlign: 'start',
    fontSize: 13, color: 'var(--text-secondary)',
    cursor: 'pointer', whiteSpace: 'nowrap',
  };

  return (
    <div className={styles.requestsPanel}>
      {/* ── Search ── one field, searches whichever list is showing ── */}
      <div style={{ padding: '10px 12px 6px' }}>
        <input
          type="search"
          className={styles.searchInput}
          style={{ inlineSize: '100%', boxSizing: 'border-box' }}
          placeholder={t('contacts.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t('contacts.searchPlaceholder')}
        />
      </div>

      {/* ── Segmented control. The COUNTS are the point: an admin sees
             "Pending 3" without scrolling, which the stacked layout could
             only manage when the list happened to be above the fold. ── */}
      <div
        role="tablist"
        style={{
          display: 'flex', gap: 2, padding: '0 12px 10px',
          borderBlockEnd: '1px solid var(--border-subtle)',
        }}
      >
        {segments.map((seg) => {
          const on = view === seg.key;
          // Pending is the only count that means "you owe someone an answer".
          const nagging = seg.key === 'pending' && seg.count > 0;
          return (
            <button
              key={seg.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setView(seg.key)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '7px 6px', cursor: 'pointer',
                fontSize: 12, fontWeight: on ? 600 : 500,
                background: on ? 'var(--bg-tertiary)' : 'transparent',
                color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
                border: '1px solid ' + (on ? 'var(--border-default)' : 'transparent'),
                borderRadius: 8,
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{seg.label}</span>
              {seg.count > 0 && (
                <span
                  style={{
                    fontSize: 11, fontWeight: 700, lineHeight: 1,
                    padding: '2px 6px', borderRadius: 999,
                    background: nagging ? 'var(--color-error)' : 'var(--bg-hover)',
                    color: nagging ? '#fff' : 'var(--text-secondary)',
                  }}
                >
                  {seg.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── CONTACTS (directory) ─────────────────────── */}
      {view === 'contacts' && (
      <div className={styles.requestsList}>
        {loadingContacts && (
          <div className={styles.emptyState}>…</div>
        )}

        {!loadingContacts && acceptedContacts.length === 0 && (
          <div className={styles.emptyState}>{t('contacts.noContacts')}</div>
        )}

        {!loadingContacts && acceptedContacts.length > 0 && filteredAccepted.length === 0 && (
          <div className={styles.emptyState}>{t('contacts.noMatches')}</div>
        )}

        {filteredAccepted.map((contact) => {
          const otherGuid = getOtherUserGuid(contact);
          const displayName = contact.displayName || contact.screenName || 'Unknown';
          const isMenuOpen = openMenuId === contact.id;
          return (
            <div key={contact.id} className={styles.requestItem}>
              <UserAvatar
                userGuid={otherGuid}
                avatarUrl={contact.profileImageUrl || undefined}
                userName={displayName}
                size="md"
                showPresence
              />

              <div className={styles.requestInfo} style={{ flex: 1, minWidth: 0 }}>
                <span className={styles.requestName}>{displayName}</span>
                {contact.screenName && (
                  <span className={styles.requestHandle}>@{contact.screenName}</span>
                )}
              </div>

              {/* "..." overflow menu */}
              <div style={{ position: 'relative', flexShrink: 0 }} ref={isMenuOpen ? menuRef : undefined}>
                <button
                  type="button"
                  onClick={() => setOpenMenuId(isMenuOpen ? null : contact.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 32, height: 32,
                    background: isMenuOpen ? 'var(--bg-tertiary)' : 'transparent',
                    border: 'none', borderRadius: 6,
                    cursor: 'pointer', color: 'var(--text-tertiary)',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!isMenuOpen) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={(e) => { if (!isMenuOpen) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                  </svg>
                </button>

                {isMenuOpen && (
                  <div style={{
                    position: 'absolute', insetBlockStart: '100%', insetInlineEnd: 0,
                    marginBlockStart: 4,
                    background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                    borderRadius: 8, boxShadow: 'var(--shadow-lg)', zIndex: 20,
                    display: 'flex', flexDirection: 'column',
                    minWidth: 170, overflow: 'hidden',
                  }}>
                    {/* View Profile */}
                    <button
                      type="button"
                      style={menuItemStyle}
                      onClick={() => { setOpenMenuId(null); navigate(`/profile/${otherGuid}`); }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                      </svg>
                      {t('contacts.viewProfile')}
                    </button>

                    {/* Chat */}
                    <button
                      type="button"
                      style={menuItemStyle}
                      onClick={() => { setOpenMenuId(null); onStartChat(otherGuid); }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      {t('contacts.startChat')}
                    </button>

                    {/* Divider */}
                    <div style={{ height: 1, background: 'var(--border-subtle)', margin: '2px 0' }} />

                    {/* Remove Contact */}
                    <button
                      type="button"
                      style={menuItemStyle}
                      onClick={() => {
                        setOpenMenuId(null);
                        setConfirmAction({ type: 'remove', contactId: contact.id, userGuid: otherGuid, name: displayName });
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" />
                        <line x1="23" y1="11" x2="17" y2="11" />
                      </svg>
                      {t('contacts.removeContact')}
                    </button>

                    {/* Block */}
                    <button
                      type="button"
                      style={{ ...menuItemStyle, color: 'var(--status-error)' }}
                      onClick={() => {
                        setOpenMenuId(null);
                        setConfirmAction({ type: 'block', contactId: contact.id, userGuid: otherGuid, name: displayName });
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                      </svg>
                      {t('contacts.block')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* ── PENDING (inbox — the only actionable list) ── */}
      {view === 'pending' && (
      <div className={styles.requestsList}>
        {requests.length === 0 && (
          <div className={styles.emptyState}>{t('page.noRequests')}</div>
        )}

        {requests.length > 0 && filteredPending.length === 0 && (
          <div className={styles.emptyState}>{t('contacts.noMatches')}</div>
        )}

        {filteredPending.map((req) => (
          <div key={req.id} className={styles.requestItem}>
            <UserAvatar
              userGuid={req.requesterGuid}
              avatarUrl={req.profileImageUrl || undefined}
              userName={req.displayName || req.screenName || '?'}
              size="md"
              showPresence
            />

            <div className={styles.requestInfo} style={{ flex: 1, minWidth: 0 }}>
              <span className={styles.requestName}>
                {req.displayName || req.screenName || 'Unknown'}
              </span>
              {req.screenName && (
                <span className={styles.requestHandle}>@{req.screenName}</span>
              )}
              <span className={styles.requestDate}>
                {new Date(req.requestedAt).toLocaleDateString()}
              </span>
            </div>

            <div className={styles.requestActions}>
              <button
                type="button"
                className={styles.acceptBtn}
                onClick={() => onAccept(req.id)}
                title={t('contacts.accept')}
              >
                {t('contacts.accept')}
              </button>
              <button
                type="button"
                className={styles.rejectBtn}
                onClick={() => onReject(req.id)}
                title={t('contacts.reject')}
              >
                {t('contacts.reject')}
              </button>
            </div>
          </div>
        ))}
      </div>

      )}

      {/* ── SENT (status check) ─────────────────────────
          DEV-1485 surfaced these at all; DEV-1486 gives them their own segment.
          No Accept/Reject — an outgoing request is not yours to answer. Cancel
          reuses the reject endpoint (cancelling your own request IS rejecting
          that row). */}
      {view === 'sent' && (
      <div className={styles.requestsList}>
        {sentRequests.length === 0 && (
          <div className={styles.emptyState}>{t('contacts.noSentRequests')}</div>
        )}

        {sentRequests.length > 0 && filteredSent.length === 0 && (
          <div className={styles.emptyState}>{t('contacts.noMatches')}</div>
        )}

        {filteredSent.map((req) => (
          <div key={req.id} className={styles.requestItem}>
            <UserAvatar
              userGuid={req.recipientGuid}
              avatarUrl={req.profileImageUrl || undefined}
              userName={req.displayName || req.screenName || '?'}
              size="md"
              showPresence
            />

            <div className={styles.requestInfo} style={{ flex: 1, minWidth: 0 }}>
              <span className={styles.requestName}>
                {req.displayName || req.screenName || 'Unknown'}
              </span>
              {req.screenName && (
                <span className={styles.requestHandle}>@{req.screenName}</span>
              )}
              <span className={styles.requestDate}>
                {t('contacts.awaitingReply')} · {new Date(req.requestedAt).toLocaleDateString()}
              </span>
            </div>

            <div className={styles.requestActions}>
              <button
                type="button"
                className={styles.rejectBtn}
                onClick={() => onReject(req.id)}
                title={t('contacts.cancelRequest')}
              >
                {t('contacts.cancelRequest')}
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* ── Confirm dialog ───────────────────────────── */}
      {confirmAction && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200000,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmAction(null); }}
        >
          <div style={{
            background: 'var(--bg-tertiary)', borderRadius: 12, padding: '24px',
            width: '90%', maxWidth: 360,
            border: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-lg)',
            textAlign: 'center',
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              {confirmAction.type === 'remove' ? t('contacts.removeContact') : t('contacts.block')}
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {confirmAction.type === 'remove'
                ? t('contacts.confirmRemove', { name: confirmAction.name })
                : t('contacts.confirmBlock', { name: confirmAction.name })
              }
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                type="button"
                className={styles.rejectBtn}
                onClick={() => setConfirmAction(null)}
                style={{ minWidth: 80 }}
              >
                {t('contacts.cancel')}
              </button>
              <button
                type="button"
                className={styles.rejectBtn}
                onClick={() => {
                  if (confirmAction.type === 'remove') {
                    handleRemoveContact(confirmAction.contactId);
                  } else {
                    handleBlockUser(confirmAction.userGuid);
                  }
                }}
                style={{ minWidth: 80, background: 'var(--status-error)', color: '#fff', borderColor: 'var(--status-error)' }}
              >
                {confirmAction.type === 'remove' ? t('contacts.removeContact') : t('contacts.block')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
